// src/services/auditLogService.js

import { repo } from "../dbservice/repo";

/* ═══════════════════════════════════════════════════════════════════════════
   STANDALONE EXPORTS  (backward-compatible — existing callers unchanged)
═══════════════════════════════════════════════════════════════════════════ */

/**
 * Write a single audit log entry via repo.
 * Drop-in replacement for the old Firebase addDoc version.
 *
 * @param {object} actionData
 * @returns {Promise<boolean>}  true = success, false = failed (queued locally)
 */
export const logAction = async (actionData) => {
  try {
    const id = `audit_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    await repo.upsertData("auditLogs", id, {
      ...actionData,
      timestamp: new Date().toISOString(),
    });

    console.log("Audit log recorded with ID:", id);
    return true;
  } catch (error) {
    console.error("Error recording audit log:", error);

    // Fallback — queue in localStorage if repo write fails
    try {
      const failedLogs = JSON.parse(
        localStorage.getItem("failedAuditLogs") || "[]",
      );
      failedLogs.push({
        ...actionData,
        timestamp: new Date().toISOString(),
      });
      localStorage.setItem("failedAuditLogs", JSON.stringify(failedLogs));
    } catch (storageErr) {
      console.error("localStorage fallback also failed:", storageErr);
    }

    return false;
  }
};

/**
 * Retry all locally-queued failed audit logs.
 * Drop-in replacement for the old Firebase batch version.
 *
 * @returns {Promise<void>}
 */
export const retryFailedLogs = async () => {
  let failedLogs = [];

  try {
    failedLogs = JSON.parse(
      localStorage.getItem("failedAuditLogs") || "[]",
    );
  } catch {
    console.error("Could not parse failedAuditLogs from localStorage.");
    return;
  }

  if (failedLogs.length === 0) return;

  try {
    await Promise.all(
      failedLogs.map((log) => {
        const id = `audit_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        return repo.upsertData("auditLogs", id, {
          ...log,
          timestamp: new Date().toISOString(),
        });
      }),
    );

    localStorage.removeItem("failedAuditLogs");
    console.log(`Successfully retried ${failedLogs.length} failed audit logs.`);
  } catch (error) {
    console.error("Error retrying failed audit logs:", error);
  }
};

/* ═══════════════════════════════════════════════════════════════════════════
   CLASS  (used by AuditLogs.jsx page — subscription + bulk delete + helpers)
═══════════════════════════════════════════════════════════════════════════ */

class AuditLogService {
  constructor() {
    this.collectionName = "auditLogs";
  }

  /* ─────────────────────────────────────────────────────────────────────────
     AUTHORIZATION GUARD
  ───────────────────────────────────────────────────────────────────────── */

  /**
   * Throws if the acting user is not a superadmin.
   */
  _assertSuperAdmin(currentUser) {
    
    const userId = currentUser?.uid || currentUser?.id;

    if (!userId) {
      throw new Error("Not authenticated.");
    }


    const userRole = currentUser.role || currentUser.user_metadata?.role;
    
    if (userRole !== "superadmin") {
      throw new Error(
        "Access denied: Super Administrator clearance required.",
      );
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
     WRITE — thin wrapper that delegates to the standalone export
     so internal callers can use either the class or the function
  ───────────────────────────────────────────────────────────────────────── */

  /**
   * Log a single action. Delegates to the standalone `logAction` export.
   * @param {object} actionData
   * @returns {Promise<boolean>}
   */
  async logAction(actionData) {
    return logAction(actionData);
  }

  /**
   * Retry all locally-queued failed logs.
   * Delegates to the standalone `retryFailedLogs` export.
   * @returns {Promise<void>}
   */
  async retryFailedLogs() {
    return retryFailedLogs();
  }

  /* ─────────────────────────────────────────────────────────────────────────
     REAL-TIME SUBSCRIPTION
  ───────────────────────────────────────────────────────────────────────── */

  /**
   * Subscribe to all audit logs ordered by timestamp descending.
   * repo decides: Supabase realtime OR Firestore onSnapshot.
   *
   * @param {object}   currentUser
   * @param {Function} callback    - called with normalised log[]
   * @returns {Function}             unsubscribe
   */
  subscribeToAuditLogs(currentUser, callback) {
    this._assertSuperAdmin(currentUser);

    return repo.subscribeToUpdates(
      this.collectionName,
      [],                                        // no filters — fetch all
      [{ field: "timestamp", order: "desc" }],   // newest first
      null,                                      // no hard limit
      (rawList) => {
        const normalised = rawList.map((raw) => this._normalise(raw));
        callback(normalised);
      },
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     DELETE
  ───────────────────────────────────────────────────────────────────────── */

  /**
   * Delete a single audit log entry.
   * @param {string} id
   */
  async deleteLog(id) {
    if (!id) throw new Error("Log ID is required.");
    return await repo.deleteData(this.collectionName, id);
  }

  /**
   * Delete multiple audit log entries in parallel.
   * @param {string[]} ids
   * @param {object}   currentUser
   * @returns {{ deleted: number }}
   */
  async bulkDeleteLogs(ids, currentUser) {
    this._assertSuperAdmin(currentUser);

    if (!ids || ids.length === 0) {
      throw new Error("No log IDs provided for deletion.");
    }

    await Promise.all(
      ids.map((id) => repo.deleteData(this.collectionName, id)),
    );

    return { deleted: ids.length };
  }

  /* ─────────────────────────────────────────────────────────────────────────
     PURE DATA HELPERS
  ───────────────────────────────────────────────────────────────────────── */

  /**
   * Extract unique, non-empty user emails from a normalised log list.
   * @param   {object[]} logs
   * @returns {string[]}
   */
  extractUniqueUsers(logs) {
    const seen = new Set();
    return logs
      .map((l) => l.userEmail)
      .filter((email) => {
        if (!email || seen.has(email)) return false;
        seen.add(email);
        return true;
      });
  }

  /**
   * Apply search + filter predicates to an in-memory log list.
   *
   * @param {object[]} logs
   * @param {object}   filters
   * @param {string}   filters.searchTerm
   * @param {string}   filters.selectedAction  - 'all' | actionType string
   * @param {string}   filters.selectedUser    - 'all' | email string
   * @returns {object[]}
   */
  applyFilters(
    logs,
    { searchTerm = "", selectedAction = "all", selectedUser = "all" },
  ) {
    let result = logs;

    // Text search across all relevant fields
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      result = result.filter((log) =>
        [
          log.userEmail,
          log.actionType,
          log.entityType,
          log.entityId,
          log.description,
        ]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(term)),
      );
    }

    // Action type filter
    if (selectedAction !== "all") {
      result = result.filter((log) => log.actionType === selectedAction);
    }

    // User filter
    if (selectedUser !== "all") {
      result = result.filter((log) => log.userEmail === selectedUser);
    }

    return result;
  }

  /**
   * Format a JS Date / Firestore Timestamp / ISO string into a
   * human-readable locale string.
   *
   * @param {Date|object|string|null} date
   * @returns {string}
   */
  formatDate(date) {
    if (!date) return "—";
    try {
      const d = date?.toDate ? date.toDate() : new Date(date);
      return d.toLocaleString("en-IN", {
        day:    "2-digit",
        month:  "short",
        year:   "numeric",
        hour:   "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "—";
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
     PRIVATE
  ───────────────────────────────────────────────────────────────────────── */

  /**
   * Normalise a raw Firestore / Supabase document into the UI shape.
   * Handles both Firestore Timestamps and ISO strings for `timestamp`.
   */
  _normalise(raw) {
    let timestamp = null;
    try {
      if (raw.timestamp?.toDate) {
        timestamp = raw.timestamp.toDate();
      } else if (raw.timestamp) {
        timestamp = new Date(raw.timestamp);
      }
    } catch { /* ignore parse errors */ }

    return {
      id:          raw.id,
      timestamp,
      userEmail:   raw.userEmail   || raw.performedBy || null,
      actionType:  raw.actionType  || raw.action      || "unknown",
      entityType:  raw.entityType  || null,
      entityId:    raw.entityId    || null,
      description: raw.description || raw.details     || null,
    };
  }
}

/* ── Singleton export (used by AuditLogs.jsx page) ──────────────────────── */
export const auditLogService = new AuditLogService();